import { Form, Spin } from 'antd5';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TaskAssetPicker } from 'components/choosers';
import {
  getRefetchQueriesForTaskCreation,
  getTaskTargetInput,
  messageContent,
} from 'components/CreateTask/helpers';
import { Button, useMessage } from 'components/ui';
import { useWCNotificationSubscriptionPrompt } from 'horizon/routes/WorkOrders/NotificationSubscriptions';
import {
  AtlasGqlInspectionSiteType,
  useCreateTaskMutation,
  useGetInspectionTypesQuery,
  useGetWorkContainerFieldsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useAuthContext } from 'utils/auth';
import {
  ASSET_TYPE_SITE,
  INSPECT_TASK,
  INSPECTION_TASK,
  INTERNAL_BLADE_INSPECTION_TASK,
  OTHER_TASK,
  REPAIR_TASK,
  TASK_TYPE_INTERNAL_BLADE_INSPECTION,
} from 'utils/constants';
import { useFeatures } from 'utils/features';
import { BOP_INSPECTIONS } from 'utils/release-toggles';
import { useStandardFields } from 'utils/useStandardFields';

import { SpinContainer, StyledModal } from './DetailedTaskForm.style';
import { FormInputs } from './FormInputs';
import { descriptionField, forOrganizationField, sortFields } from './helpers';

const typeDict = {
  [OTHER_TASK]: {
    title: 'Other',
    titleKey: 'tasks.other',
    type: 'OTHER',
  },
  [REPAIR_TASK]: {
    title: 'Repair',
    titleKey: 'workOrder.repair',
    type: 'REPAIR',
  },
  [INTERNAL_BLADE_INSPECTION_TASK]: {
    title: 'Internal Blade Inspection',
    titleKey: 'tasks.internalBladeInspection',
    type: TASK_TYPE_INTERNAL_BLADE_INSPECTION,
  },
  [INSPECT_TASK]: {
    title: 'Inspect',
    titleKey: 'tasks.inspect',
    type: 'INSPECT',
  },
  [INSPECTION_TASK]: {
    title: 'Inspection',
    titleKey: 'inspection.title',
    type: 'INSPECTION',
  },
};

function isRepairOrInspect(type) {
  if (type === REPAIR_TASK || type === INSPECT_TASK) {
    return true;
  }
  return false;
}

function isInspection(type) {
  if (type === INSPECTION_TASK) {
    return true;
  }
  return false;
}

export function DetailedTaskForm({
  title,
  visible,
  handleClose,
  parentId,
  type,
  forOrganization = null,
  damageBranchId,
  horizonDamageId,
  assetId = '',
  queriesToRefetch = [],
  disabled = false,
  inspectionId,
}) {
  const { t } = useTranslation();
  const message = useMessage();
  const { openPrompt } = useWCNotificationSubscriptionPrompt();
  const { user } = useAuthContext();
  const { hasReleaseToggle } = useAccountContext();

  const [form] = Form.useForm();

  const [initialAssetId, setInitialAssetId] = useState(assetId);
  const [selectedAssetId, setSelectedAssetId] = useState(undefined);
  const [selectedAssetInfo, setSelectedAssetInfo] = useState(undefined);
  const [createTaskLoading, setCreateTaskLoading] = useState(false);

  const { INSPECTION_DELIVERY, CREATE_OTHER_SITE_TASK } = useFeatures().features;
  const hasBopInspections = hasReleaseToggle(BOP_INSPECTIONS);

  const { data: workContainerFieldsData, loading: workContainerFieldsLoading } =
    useGetWorkContainerFieldsQuery({
      variables: { type },
    });

  const queryRefetch = getRefetchQueriesForTaskCreation({ horizonDamageId, damageBranchId });

  const [createTask] = useCreateTaskMutation({
    onCompleted({ createTask: createdTask }) {
      if (createdTask) {
        const { id, number, __typename } = createdTask;
        openPrompt({
          content: messageContent({ id, number: number ?? 0, type: __typename ?? 'Task' }),
          workContainer: createdTask,
        });

        setCreateTaskLoading(false);
        form.resetFields();
        handleClose({ id, number: number ?? 0, type: __typename ?? 'Task' });
      }
    },
    onError: e => {
      console.log(e);
      message.error(t('tasks.problemCreatingTask', 'There was a problem creating the Task.'));
      setCreateTaskLoading(false);
    },
    refetchQueries: [...queryRefetch, ...queriesToRefetch],
  });

  const standardFields = useStandardFields();

  const filteredFields = useMemo(() => {
    const workContainerFields =
      workContainerFieldsData?.workContainerFields?.filter(field => {
        // vendor org is only available on work orders
        if (field.slug === 'vendor_organization' && field.standard) {
          return false;
        }
        return true;
      }) ?? [];

    // Do not show Parent when opening from WO Detail page
    return isRepairOrInspect(type)
      ? workContainerFields
      : workContainerFields.filter(({ slug }) => slug !== 'parent');
  }, [workContainerFieldsData, type]);

  const additionalFields = useMemo(() => {
    return isInspection(type) && INSPECTION_DELIVERY
      ? [descriptionField, forOrganizationField]
      : [descriptionField];
  }, [INSPECTION_DELIVERY, type]);

  const allFields = useMemo(
    () => [...additionalFields, ...filteredFields],
    [additionalFields, filteredFields]
  );

  const shouldUseBackendSiteInspectionTypes =
    hasBopInspections &&
    type === INSPECTION_TASK &&
    selectedAssetInfo?.assetTypeName === ASSET_TYPE_SITE &&
    Boolean(selectedAssetInfo?.assetTypeId);

  const { data: selectedSiteInspectionTypeData, loading: selectedSiteInspectionTypesLoading } =
    useGetInspectionTypesQuery({
      variables: {
        siteType: selectedAssetInfo?.siteType,
        filterBy: selectedAssetInfo?.assetTypeId
          ? [{ key: 'assetTypeId', values: [selectedAssetInfo.assetTypeId] }]
          : [],
      },
      skip: !shouldUseBackendSiteInspectionTypes,
    });

  const hasValidSelectedSiteInspectionTypes =
    (selectedSiteInspectionTypeData?.inspectionTypes?.length ?? 0) > 0;
  const isCheckingSelectedSiteInspectionTypes =
    shouldUseBackendSiteInspectionTypes && selectedSiteInspectionTypesLoading;

  const uneditableFields = useMemo(
    () => ({
      created: moment(),
      created_by: user?.email,
      task_type: typeDict[type] ? t(typeDict[type].titleKey, typeDict[type].title) : undefined,
    }),
    [type, user, t]
  );

  // This useEffect is responsible for setting initial values of the form.
  // We have to set the initial assetId this way so that when we set the asset pickers it
  // will have an asset as a form value.
  useEffect(() => {
    const initValues = {};
    allFields.forEach(field => {
      if (uneditableFields[field.slug]) {
        initValues[field.slug] = uneditableFields[field.slug];
      }
    });
    if (initialAssetId) {
      initValues['assetId'] = initialAssetId;
    }

    // The form can't have its fields set until its rendered. Rendering happens based on visibility.
    if (form && visible) {
      form.setFieldsValue(initValues);
    }
  }, [form, allFields, uneditableFields, initialAssetId, visible]);

  useEffect(() => {
    if (
      shouldUseBackendSiteInspectionTypes &&
      !selectedSiteInspectionTypesLoading &&
      !hasValidSelectedSiteInspectionTypes &&
      selectedAssetInfo?.assetId === selectedAssetId
    ) {
      setSelectedAssetId(undefined);
      form.setFieldsValue({ assetId: undefined });
    }
  }, [
    form,
    hasValidSelectedSiteInspectionTypes,
    selectedAssetId,
    selectedAssetInfo,
    selectedSiteInspectionTypesLoading,
    shouldUseBackendSiteInspectionTypes,
  ]);

  const getValueFromAssetChooser = assetInfo => {
    if (!assetInfo) {
      setSelectedAssetInfo(undefined);
      setSelectedAssetId(undefined);
      return undefined;
    }

    const { assetId, assetTypeId, assetTypeName, siteType, isLocation, isBlade } = assetInfo;
    setSelectedAssetInfo(assetInfo);

    const isSolarSite =
      assetTypeName === ASSET_TYPE_SITE && siteType === AtlasGqlInspectionSiteType.Solar;
    const canUseBackendSiteInspectionTypes =
      hasBopInspections &&
      type === INSPECTION_TASK &&
      assetTypeName === ASSET_TYPE_SITE &&
      Boolean(assetTypeId);
    const canChooseLocation =
      (CREATE_OTHER_SITE_TASK && type === OTHER_TASK) ||
      (type === INSPECTION_TASK &&
        (hasBopInspections ? canUseBackendSiteInspectionTypes : isSolarSite));

    if (isLocation && !canChooseLocation) {
      setSelectedAssetId(undefined);
      return undefined;
    }

    if (type === INTERNAL_BLADE_INSPECTION_TASK && !isBlade) {
      setSelectedAssetId(undefined);
      return undefined;
    }

    setSelectedAssetId(assetId);
    return assetId;
  };

  async function handleCreateTask() {
    setCreateTaskLoading(true);
    const values = form.getFieldsValue();
    const formattedValues = sortFields({
      formValues: values,
      allFields,
      standardFieldKeys: {
        ...standardFields[type],
        parent: { dataIndex: 'workOrderId' },
        description: { dataIndex: 'description' },
        for_organization: { dataIndex: 'forOrganization' },
      },
      uneditableFields,
    });

    const input = isRepairOrInspect(type)
      ? {
          damageId: parentId,
          damageBranchId,
          ...(horizonDamageId ? { horizonDamageId } : {}),
          assetId: initialAssetId,
        }
      : {
          workOrderId: parentId,
          assetId: selectedAssetId ? selectedAssetId : initialAssetId,
        };

    await createTask({
      variables: {
        input: {
          type,
          ...(forOrganization && { forOrganization }),
          additionalFields: Object.entries(formattedValues.custom).map(([key, value]) => ({
            fieldId: key,
            ...value,
          })),
          ...formattedValues.standard,
          ...input,
          ...(inspectionId || horizonDamageId
            ? { targets: getTaskTargetInput({ inspectionId, horizonDamageId }) }
            : {}),
        },
      },
    });
  }

  async function validateFields() {
    try {
      await form.validateFields();
      if (shouldUseBackendSiteInspectionTypes) {
        if (isCheckingSelectedSiteInspectionTypes) {
          return;
        }

        if (!hasValidSelectedSiteInspectionTypes) {
          form.setFields([
            {
              name: 'assetId',
              errors: [getErrorMessage()],
            },
          ]);
          return;
        }
      }
      handleCreateTask();
    } catch (err) {
      // err
    }
  }

  const getErrorMessage = () => {
    if (type === INTERNAL_BLADE_INSPECTION_TASK) {
      return t('workOrder.chooseBlade', 'Please choose a blade');
    }
    return CREATE_OTHER_SITE_TASK && type === OTHER_TASK
      ? t('workOrder.chooseSite', 'Please choose a site')
      : t('workOrder.chooseAsset', 'Please choose an asset');
  };

  return (
    <StyledModal
      title={title}
      className="testid-create-task-modal"
      open={visible}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        <>
          <Button data-testid="cancel-btn" onClick={handleClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            loading={createTaskLoading || isCheckingSelectedSiteInspectionTypes}
            onClick={validateFields}
            data-testid="save-btn"
          >
            {t('common.save', 'Save')}
          </Button>
        </>
      }
    >
      <Form layout="vertical" form={form}>
        {workContainerFieldsLoading ? (
          <SpinContainer>
            <Spin />
          </SpinContainer>
        ) : (
          <>
            {!isRepairOrInspect(type) && (parentId || initialAssetId) && (
              <Form.Item
                name="assetId"
                label={t('table.asset', 'Asset')}
                getValueFromEvent={getValueFromAssetChooser}
                rules={[
                  {
                    required: true,
                    message: getErrorMessage(),
                  },
                ]}
              >
                <TaskAssetPicker
                  disableAll={disabled}
                  taskId={parentId}
                  selectedAssetId={selectedAssetId}
                  initialAssetId={initialAssetId}
                  setInitialAssetId={setInitialAssetId}
                  showLoadingIndicator={true}
                  onChange={getValueFromAssetChooser}
                  defaults={{
                    settings: {
                      location: { multiple: false },
                      turbine: { multiple: false },
                      blade: { multiple: false, visible: true },
                    },
                    block: true,
                  }}
                />
              </Form.Item>
            )}
            <FormInputs
              fields={allFields}
              form={form}
              isRepairOrInspect={isRepairOrInspect}
              type={type}
              uneditableFields={uneditableFields}
              assetId={selectedAssetId}
            />
          </>
        )}
      </Form>
    </StyledModal>
  );
}
